/* ==========================================================================
   HLD — Colour picker (wall prints)

   Swaps the selected variant client-side: hidden form input, price, CTA
   availability, hero image, active thumbnail, sticky-bar price and the
   ?variant= URL. Registers only if <hld-colour-picker> is on the page.

   Deliberately standalone rather than folded into hld-pdp.js: this markup
   exists on 3 of 86 products, and keeping it separate means the shared PDP
   bundle is unchanged for the other 83.
   ========================================================================== */

if (!customElements.get('hld-colour-picker')) {
  class HldColourPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-hld-variants]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return; // malformed payload — leave the server-rendered state alone
      }

      this.swatches = Array.from(this.querySelectorAll('.hld-colour-picker__swatch'));
      this.nameEls = Array.from(this.querySelectorAll('[data-colour-name]'));

      this.swatches.forEach((btn) => {
        btn.addEventListener('click', () => this.select(btn));
        btn.addEventListener('keydown', (e) => this.onKeydown(e, btn));
      });
    }

    /* Arrow keys move between swatches, per the radiogroup role. */
    onKeydown(event, btn) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
      if (keys.indexOf(event.key) === -1) return;
      event.preventDefault();

      const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      const i = this.swatches.indexOf(btn);
      const next = this.swatches[(i + step + this.swatches.length) % this.swatches.length];
      next.focus();
      this.select(next);
    }

    select(btn) {
      if (btn.dataset.oos === '1') return;

      const colour = btn.dataset.colourValue;
      const variant = this.variants.find((v) => v.colour === colour);
      if (!variant) return;

      this.swatches.forEach((s) => s.setAttribute('aria-checked', String(s === btn)));
      this.nameEls.forEach((el) => {
        el.textContent = colour;
      });

      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateGallery(variant);
      this.updateUrl(variant);

      /* Anything else listening for a variant change (Judge.me, analytics)
         gets the same event Horizon dispatches. */
      document.dispatchEvent(
        new CustomEvent('variant:change', { detail: { variant: variant }, bubbles: true })
      );
    }

    updateForm(variant) {
      const input = document.querySelector('.hld-atf__form input[name="id"]');
      if (input) input.value = variant.id;

      const cta = document.querySelector('.hld-atf__cta');
      if (cta) {
        cta.disabled = !variant.available;
        cta.classList.toggle('hld-atf__cta--sold-out', !variant.available);
      }
    }

    updatePrice(variant) {
      document
        .querySelectorAll('.hld-atf__price-current, .hld-atf__sticky-price')
        .forEach((el) => {
          el.textContent = variant.price_formatted;
        });
    }

    updateGallery(variant) {
      /* No early return on a missing image — the rail below still has to be
         re-filtered, otherwise picking an image-less colourway would leave the
         previous colourway's thumbnails on screen. */
      const hero = document.querySelector('.hld-atf__hero-image');
      if (hero && variant.image) {
        hero.src = variant.image;
        hero.srcset = ''; // src alone wins once srcset is cleared
        /* The alt is server-rendered for the first colourway only, so without
           this a screen reader announces the same colour on all seven. */
        if (variant.image_alt) hero.alt = variant.image_alt;
      }

      /* Filter the rail down to this colourway and mirror the active state, so
         the gallery and the picker never disagree about which print is on
         screen. Every colourway is a different print, so showing all of them at
         once and only highlighting one reads as unrelated products.

         Thumbnails with no data-colour-value are colour-neutral and always
         stay: hld-atf tags a thumbnail only when the product has a Colour
         option, which is the same guard that renders this picker at all. */
      let shown = 0;
      document.querySelectorAll('.hld-atf__thumbnail').forEach((thumb) => {
        const thumbColour = thumb.dataset.colourValue || '';
        const matches = thumbColour === '' || thumbColour === variant.colour;

        thumb.hidden = !matches;
        if (matches) shown++;

        thumb.classList.toggle(
          'hld-atf__thumbnail--active',
          String(thumb.dataset.mediaId) === String(variant.media_id)
        );
      });

      /* A lone thumbnail under the hero looks like a rendering fault, and six
         of the seven colourways only have the one photo. */
      const rail = document.querySelector('.hld-atf__thumbnails');
      if (rail) rail.hidden = shown <= 1;
    }

    updateUrl(variant) {
      if (!window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  customElements.define('hld-colour-picker', HldColourPicker);
}
